import React, { useState, useRef, useContext } from 'react';
import {
  Paper,
  Button,
  Typography,
  Box,
  LinearProgress,
  Alert,
  Stack,
} from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import getToken from '../auth/getToken';
import { loadTestHeaders } from '../auth/loadTestAuth';
import { TranscriptionContext } from '../context/TranscriptionContext';
import PageHeader from './common/PageHeader';
import RetentionNotice from './common/RetentionNotice';
import UploadDropzone from './Transcription/UploadDropzone';
import TranscriptionSettings from './Transcription/TranscriptionSettings';
import { hinweisUebrigeDateien, uploadFehlermeldung } from '../helper/uploadErrors';

const ALLOWED_AUDIO_TYPES = [
  'audio/wav',
  'audio/mp3',
  'audio/mp4',
  'audio/flac',
  'audio/ogg',
  'audio/webm',
  'audio/aac',
  'video/mp4',
];
const ALLOWED_AUDIO_EXTENSIONS = [
  '.wav',
  '.mp3',
  '.m4a',
  '.mp4',
  '.flac',
  '.ogg',
  '.webm',
  '.aac',
];
const MAX_AUDIO_SIZE = 100 * 1024 * 1024;

function validateCandidate(candidate) {
  if (candidate.size > MAX_AUDIO_SIZE) {
    const sizeMB = (candidate.size / (1024 * 1024)).toFixed(1);
    const maxSizeMB = MAX_AUDIO_SIZE / (1024 * 1024);
    return `Datei "${candidate.name}" zu groß: ${sizeMB} MB. Maximum: ${maxSizeMB} MB`;
  }
  if (!ALLOWED_AUDIO_TYPES.includes(candidate.type)) {
    const ext = '.' + candidate.name.split('.').pop().toLowerCase();
    if (!ALLOWED_AUDIO_EXTENSIONS.includes(ext)) {
      return `Dateityp von "${candidate.name}" nicht unterstützt. Erlaubt: ${ALLOWED_AUDIO_EXTENSIONS.join(', ')}`;
    }
  }
  return null;
}

function TranscriptionView() {
  const navigate = useNavigate();
  const [fileEntries, setFileEntries] = useState([]);
  const [fileError, setFileError] = useState('');
  const [language, setLanguage] = useState('de');
  const [isUploading, setIsUploading] = useState(false);
  const [activeUploadProgress, setActiveUploadProgress] = useState(0);
  const [activeUploadIndex, setActiveUploadIndex] = useState(-1);

  const fileInputRef = useRef(null);
  const { instance } = useMsal();
  const { upsertJob } = useContext(TranscriptionContext);

  const addFiles = (list) => {
    if (!list.length) return;

    const errors = [];
    const accepted = [];
    const existingKeys = new Set(
      fileEntries.map((entry) =>
        `${entry.file.name}:${entry.file.size}:${entry.file.lastModified}`,
      ),
    );

    for (const candidate of list) {
      const key = `${candidate.name}:${candidate.size}:${candidate.lastModified}`;
      if (existingKeys.has(key)) continue;
      const err = validateCandidate(candidate);
      if (err) errors.push(err);
      else {
        accepted.push({ file: candidate, status: 'bereit', locked: false });
        existingKeys.add(key);
      }
    }
    setFileError(errors.join('\n'));
    setFileEntries((prev) => [...prev, ...accepted]);
  };

  const handleFilesChange = (event) => {
    addFiles(Array.from(event.target.files || []));
    event.target.value = '';
  };

  const handleFilesDrop = (files) => {
    addFiles(Array.from(files || []));
  };

  const removeFile = (idx) => {
    setFileEntries((prev) => prev.filter((_, i) => i !== idx));
  };

  const clearAll = () => {
    setFileEntries([]);
    setFileError('');
  };

  const enqueueFiles = async () => {
    if (fileEntries.length === 0 || isUploading) return;
    setIsUploading(true);
    setFileError('');

    try {
      const token = await getToken(instance, [
        `${process.env.REACT_APP_CLIENT_ID}/.default`,
      ]);

      for (let i = 0; i < fileEntries.length; i += 1) {
        const entry = fileEntries[i];
        if (entry.status === 'eingereiht') continue;

        setActiveUploadIndex(i);
        setActiveUploadProgress(0);
        setFileEntries((prev) =>
          prev.map((e, idx) =>
            idx === i ? { ...e, status: 'lädt hoch…', locked: true } : e,
          ),
        );

        const formData = new FormData();
        formData.append('file', entry.file);
        formData.append('language', language);

        try {
          const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/transcribe`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                ...loadTestHeaders(),
              },
              onUploadProgress: (progressEvent) => {
                const percent = progressEvent.total
                  ? Math.round(
                      (progressEvent.loaded * 100) / progressEvent.total,
                    )
                  : 0;
                setActiveUploadProgress(percent);
              },
            },
          );
          const { job_id, queue_position, status } = response.data;
          upsertJob(job_id, {
            jobId: job_id,
            status,
            queuePosition: queue_position,
            originalFilename: entry.file.name,
            language,
          });
          setFileEntries((prev) =>
            prev.map((e, idx) =>
              idx === i ? { ...e, status: 'eingereiht', locked: true } : e,
            ),
          );
        } catch (err) {
          console.error('Upload failed', err);
          setFileEntries((prev) =>
            prev.map((e, idx) =>
              idx === i ? { ...e, status: 'Fehler', locked: false } : e,
            ),
          );
          setFileError(
            (prev) => prev + `\n${uploadFehlermeldung(err, entry.file.name)}`,
          );
          const uebrige = fileEntries
            .slice(i + 1)
            .filter((e) => e.status !== 'eingereiht').length;
          const hinweis = hinweisUebrigeDateien(err, uebrige);
          if (hinweis !== null) {
            if (hinweis) setFileError((prev) => `${prev}\n${hinweis}`);
            break;
          }
        }
      }
    } finally {
      setIsUploading(false);
      setActiveUploadIndex(-1);
      setActiveUploadProgress(0);
    }
  };

  const hasEnqueuable = fileEntries.some((e) => e.status !== 'eingereiht');

  return (
    <Box sx={{ maxWidth: 840, mx: 'auto', p: { xs: 2, md: 4 } }}>
      <PageHeader
        overline="Transcribe"
        title="Neue Transkription"
        subtitle="Eine oder mehrere Dateien hochladen – sie landen alle in der Warteschlange. Sprechererkennung ist automatisch aktiv."
        action={
          <Button
            variant="contained"
            startIcon={<LibraryBooksOutlinedIcon />}
            onClick={() => navigate('/transcriptions')}
          >
            Transkriptionen
          </Button>
        }
      />

      <Stack spacing={2.5}>
        <UploadDropzone
          fileInputRef={fileInputRef}
          files={fileEntries}
          disabled={isUploading}
          accept=".wav,.mp3,.m4a,.mp4,.flac,.ogg,.webm,.aac"
          maxSizeMB={MAX_AUDIO_SIZE / (1024 * 1024)}
          onFilesChange={handleFilesChange}
          onFilesDrop={handleFilesDrop}
          onRemoveFile={removeFile}
          onClearAll={clearAll}
        />

        {fileError && (
          <Alert severity="error" sx={{ borderRadius: 2, whiteSpace: 'pre-line' }}>
            {fileError}
          </Alert>
        )}

        <RetentionNotice />

        <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3 }}>
          <TranscriptionSettings
            language={language}
            onLanguageChange={setLanguage}
            disabled={isUploading}
          />
        </Paper>

        {isUploading && activeUploadIndex >= 0 && (
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Upload {activeUploadIndex + 1}/{fileEntries.length}
              {activeUploadProgress > 0 ? `: ${activeUploadProgress}%` : ' wird gestartet …'}
            </Typography>
            <LinearProgress
              variant={activeUploadProgress > 0 ? 'determinate' : 'indeterminate'}
              value={activeUploadProgress}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        )}

        <Button
          variant="contained"
          size="large"
          onClick={enqueueFiles}
          disabled={!hasEnqueuable || isUploading}
          fullWidth
          sx={{ borderRadius: 2, py: 1.25 }}
        >
          {isUploading
            ? 'Wird hochgeladen …'
            : fileEntries.length > 1
            ? `${fileEntries.length} Dateien in Warteschlange stellen`
            : 'Transkribieren'}
        </Button>
      </Stack>
    </Box>
  );
}

export default TranscriptionView;
